import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { DirectInputContext } from './DirectInputContext';
import { ExternalInputContext } from './ExternalInputContext';

/**
 * Hook to determine which context to use based on the route
 * and provide a unified interface for accessing state
 */
export const useWineContext = () => {
  const location = useLocation();
  const isExternalMode = location.pathname.includes('/external/');
  
  // Get external context (won't throw if not available)
  const externalContext = useContext(ExternalInputContext);
  
  // Instead of using useDirectInputContext which has a throw inside it,
  // use useContext directly with the DirectInputContext
  const directContext = useContext(DirectInputContext);
  
  // Create a fallback context object
  const fallbackContext = {
    // Search and Filter State
    keywordsList: [],
    setKeywordsList: () => {},
    filter_id: '',
    setfilter_id: () => {},
    lockedProducerLiterals: [],
    setLockedProducerLiterals: () => {},
    search_partial: true,
    setSearch_partial: () => {},
    search_partialProducer: true,
    setSearch_partialProducer: () => {},
    
    // Table Data State
    fetchedWines: [],
    setFetchedWines: () => {},
    fetchedProducers: [],
    setFetchedProducers: () => {},
    selectedWineRows: [],
    setSelectedWineRows: () => {},
    mergeModalWines: [],
    setMergeModalWines: () => {},
    
    // Pagination State
    winesPage: 1,
    setWinesPage: () => {},
    producersPage: 1,
    setProducersPage: () => {},
    hasMoreWines: false,
    setHasMoreWines: () => {},
    hasMoreProducers: false,
    setHasMoreProducers: () => {},
    totalRowsOfWines: 0,
    setTotalRowsOfWines: () => {},
    totalRowsOfProducers: 0,
    setTotalRowsOfProducers: () => {},
    
    // UI State
    showMergeModal: false,
    setShowMergeModal: () => {},
    showProducerModal: false,
    setShowProducerModal: () => {},
    selectedProducerId: null,
    setSelectedProducerId: () => {},
    
    // Word Processing State
  };
  
  // Handle external mode
  if (isExternalMode && externalContext) {
    return {
      // Use fallback context as a base
      ...fallbackContext,
      
      // Override with external-specific values
      keywordsList: externalContext.externalKeywordsList || [],
      setKeywordsList: externalContext.setExternalKeywordsList,
      filter_id: externalContext.externalFilterId || '',
      setfilter_id: externalContext.setExternalFilterId,
      lockedProducerLiterals: externalContext.externalLockedProducerLiterals || [],
      setLockedProducerLiterals: externalContext.setExternalLockedProducerLiterals,
      selectedProducerId: externalContext.externalSelectedProducerId,
      setSelectedProducerId: externalContext.setExternalSelectedProducerId,
      
      // Search partial
      search_partial: externalContext.externalSearchPartial ?? true,
      setSearch_partial: externalContext.setExternalSearchPartial || (() => {}),
      search_partialProducer: externalContext.externalSearchPartialProducer ?? true,
      setSearch_partialProducer: externalContext.setExternalSearchPartialProducer || (() => {}),
      
      // Wine editing related props
      selectedWineRows: externalContext.externalSelectedWineRows || [],
      setSelectedWineRows: externalContext.setExternalSelectedWineRows || (() => {}),
      fetchedWines: externalContext.externalFetchedWines || [],
      setFetchedWines: externalContext.setExternalFetchedWines || (() => {}),
      fetchedProducers: externalContext.externalFetchedProducers || [],
      setFetchedProducers: externalContext.setExternalFetchedProducers || (() => {}),
      mergeModalWines: externalContext.externalMergeModalWines || [],
      setMergeModalWines: externalContext.setExternalMergeModalWines || (() => {}),
      showMergeModal: externalContext.externalShowMergeModal || false,
      setShowMergeModal: externalContext.setExternalShowMergeModal || (() => {}),
      showProducerModal: externalContext.externalShowProducerModal || false,
      setShowProducerModal: externalContext.setExternalShowProducerModal || (() => {}),
      
      // Pagination related props
      winesPage: externalContext.externalWinesPage || 1,
      setWinesPage: externalContext.setExternalWinesPage || (() => {}),
      producersPage: externalContext.externalProducersPage || 1,
      setProducersPage: externalContext.setExternalProducersPage || (() => {}),
      hasMoreWines: externalContext.externalHasMoreWines || false,
      setHasMoreWines: externalContext.setExternalHasMoreWines || (() => {}),
      hasMoreProducers: externalContext.externalHasMoreProducers || false,
      setHasMoreProducers: externalContext.setExternalHasMoreProducers || (() => {}),
      totalRowsOfWines: externalContext.externalTotalRowsOfWines || 0,
      setTotalRowsOfWines: externalContext.setExternalTotalRowsOfWines || (() => {}),
      totalRowsOfProducers: externalContext.externalTotalRowsOfProducers || 0,
      setTotalRowsOfProducers: externalContext.setExternalTotalRowsOfProducers || (() => {}),
      
      // Word processing related props
      
      // Flag to indicate we're in external mode
      isExternalMode: true
    };
  }
  
  // Use the direct context if available and we're in direct input mode
  if (!isExternalMode && directContext) {
    return {
      ...directContext,
      isExternalMode: false
    };
  } else {
    // Return the fallback context with isExternalMode set appropriately
    return {
      ...fallbackContext,
      isExternalMode: true
    };
  }
};
